import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../../reduxService/hooks';
import groundImage from '../../../../images/ground_pattern.svg';
import { getImgSrcWithColor, getTextureByMaterial, loadImage } from '../../../../utils/graphicEngine';
import { ISectorResponse } from '../../../api/IApiResponse';
import { SubSectorPosition } from '../../../enum/Sector';
import { MaterialCategory } from '../../../../enum/Material';
import { setDesignImg } from '../../../store/project/resultsSlice';
import { roundToTwo } from '../../InputForm/Geometry/utils';

export interface ICanvasProps {
    sectors: ISectorResponse[];
    freeboard?: number;
    waterDepth?: number;
    scale: number;
    className?: string;
    onClickSector?: (sector: IInspectMaterial) => void;
}

export interface IInspectMaterial {
    color: string;
    materialName: string;
    length: number;
    allowedShearStress: number;
    roughness: number;
    isGeotextile?: boolean;
    category?: MaterialCategory;
}

interface ICanvasSector {
    channel: number;
    moveToX: number;
    moveToY: number;
    angle: number;
    lineToX: number;
    length: number;
    lineToY: number;
    height: number;
    position?: SubSectorPosition;
    width: number;
    img: HTMLImageElement;
    color: string;
    error?: boolean;
    withTwoLines?: boolean;
    subSectors?: ICanvasSector[];
}

interface IImageSectorCanvas {
    channel: number;
    imgSrc?: HTMLImageElement;
    subSector?: IImageSubSectorCanvas[];
}

interface IImageSubSectorCanvas {
    position: SubSectorPosition;
    imgSrc?: HTMLImageElement;
}

//---------- Setup ---------//
const waterColor = '#83d7ee';
const errorColor = 'red';
const heightSoil = 2;
const borderWidth = 1;
//Canvas tag
const lineWidth = 13;
//Second line
const colorSecondLine = 'yellow';
const secondLineWidth = 5;
const secondLineOffset = 5;
//Dotted Lines
const colordottedLine = 'red';
const dottedLineWidth = 1;
//Indicator
const indicatorHeight = 55;
const indicatorWidth = 55;
const font = '11px Arial';
const colorIndicator = 'red';
const indicatorLineWidth = 1;
//---------- Setup ---------//

const lowerSector = 4;

let canvasSectors: ICanvasSector[];
let sectorsToShow: ISectorResponse[];

let _scale: number;

const Canvas = (props: ICanvasProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const canvas = React.useRef<HTMLCanvasElement>(null);
    const { sectors, freeboard, scale, className, waterDepth, onClickSector } = props;
    let _secondLineOffset = lineWidth + secondLineOffset;

    sectorsToShow = sectors.filter(x => x.isActive);

    let _canvasSectors: ICanvasSector[];
    let widthAllSector: number = 0;
    let heightAllSector: number = 0;
    let width: number = 0;
    let height: number = 0;
    let soilImageSrc: HTMLImageElement;
    let imgSrc: IImageSectorCanvas[] = [];
    let ctx: CanvasRenderingContext2D;

    let isThereSubsector = false;

    const getCanvasAttributes = () => {
        let heightRightSector = 0;
        let heightLeftSector = 0;
        sectorsToShow.forEach(x => {
            widthAllSector += x.width;
            if (x.channel > 4) {
                heightRightSector += x.height;
            } else {
                heightLeftSector += x.height;
            }
        });
        heightAllSector = Math.max(heightRightSector, heightLeftSector);
        width = borderWidth + widthAllSector + borderWidth;
        height = heightAllSector + heightSoil;
    };

    const sortSectorAndSubSector = () => {
        sectorsToShow = sectorsToShow.sort((a, b) => a.channel - b.channel);
        sectorsToShow = sectorsToShow.map(sec => ({
            ...sec,
            subSectors: sec.subSectors?.length! > 0 ? [...sec.subSectors!].sort((a, b) => a.position - b.position) : [],
        }));
    };

    sortSectorAndSubSector();
    getCanvasAttributes();

    React.useEffect(() => {
        canvas.current?.addEventListener('click', e => {
            let rect = canvas.current!.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            canvasSectors.forEach(sec => {
                if (sec.subSectors?.length! > 0) {
                    sec.subSectors?.forEach(sub => {
                        createLine(
                            [],
                            _scale * (indicatorWidth / _scale + borderWidth + sub.moveToX),
                            _scale * (indicatorHeight / _scale + sub.moveToY),
                            _scale * (indicatorWidth / _scale + borderWidth + sub.lineToX),
                            _scale * (indicatorHeight / _scale + sub.lineToY),
                            lineWidth + 20,
                            'transparent',
                        );
                        let result = ctx.isPointInStroke(mouseX, mouseY);
                        if (result) {
                            let sector = sectorsToShow.find(x => x.channel == sec.channel)!;
                            let subSector = sector.subSectors?.find(x => x.position == sub.position)!;
                            onClickSector!({
                                color: subSector.material?.color!,
                                materialName: subSector.material?.name!,
                                length: subSector.length!,
                                allowedShearStress: subSector.material?.isVegetated
                                    ? subSector.material?.physicalData![0].vegetatedAllowShearStress!
                                    : subSector.material?.physicalData![0].allowShearStress!,
                                roughness: subSector.material?.isVegetated ? subSector.material?.physicalData![0].vegetatedRoughness! : subSector.material?.physicalData![0].roughness!,
                                isGeotextile: subSector.material?.isGeotextileFilter,
                                category: subSector.material?.category,
                            });
                        }
                    });
                } else {
                    createLine(
                        [],
                        _scale * (indicatorWidth / _scale + borderWidth + sec.moveToX),
                        _scale * (indicatorHeight / _scale + sec.moveToY),
                        _scale * (indicatorWidth / _scale + borderWidth + sec.lineToX),
                        _scale * (indicatorHeight / _scale + sec.lineToY),
                        lineWidth + 20,
                        'transparent',
                    );
                    let result = ctx.isPointInStroke(mouseX, mouseY);
                    if (result) {
                        let sector = sectorsToShow.find(x => x.channel == sec.channel)!;
                        onClickSector!({
                            color: sector.material?.color!,
                            materialName: sector.material?.name!,
                            length: sector.length,
                            allowedShearStress: sector.material?.isVegetated ? sector.material?.physicalData![0].vegetatedAllowShearStress! : sector.material?.physicalData![0].allowShearStress!,
                            roughness: sector.material?.isVegetated ? sector.material?.physicalData![0].vegetatedRoughness! : sector.material?.physicalData![0].roughness!,
                            isGeotextile: sector.material?.isGeotextileFilter,
                            category: sector.material?.category,
                        });
                    }
                }
            });
        });
    }, []);

    React.useEffect(() => {
        Promise.all([...sectorsToShow.map(sector => loadImageToSector(sector)), loadSoilImage(groundImage)]).then(() => {
            cleanCanvas();
            getCanvasSectors();
            if (freeboard != undefined) {
                createWaterSection();
            }
            createSoil();
            createGeoTextileFilterLine();
            createSectors();
            createChannel();
            dispatch(setDesignImg(canvas.current!.toDataURL('image/png')));
        });
    }, [props]);

    const cleanCanvas = () => {
        let canvasEle = canvas.current!;
        ctx = canvasEle.getContext('2d')!;
        ctx.clearRect(0, 0, canvasEle.width, canvasEle.height);
    };

    const loadSoilImage = async (_imgSrc: string): Promise<void> => {
        soilImageSrc = await loadImage(_imgSrc);
    };

    const loadImageToSector = async (sec: ISectorResponse): Promise<void> => {
        let htmlImageEl;
        let color = sec.material?.color!;
        let texture = getTextureByMaterial(sec.material!.type, sec.material!.category);
        if (!!texture) {
            let imgSrcWithColor = getImgSrcWithColor(texture, color);
            htmlImageEl = await loadImage(imgSrcWithColor);
        }
        let el: IImageSectorCanvas = {
            channel: sec.channel,
            imgSrc: htmlImageEl,
            subSector: [],
        };
        if (sec.subSectors?.length! > 0) {
            for (const subSec of sec.subSectors!) {
                let htmlImageEl;
                let color = subSec.material?.color!;
                let texture = getTextureByMaterial(subSec.material!.type, subSec.material!.category);
                if (!!texture) {
                    let imgSrcWithColor = getImgSrcWithColor(texture, color);
                    htmlImageEl = await loadImage(imgSrcWithColor);
                }
                el.subSector!.push({
                    position: subSec.position,
                    imgSrc: htmlImageEl,
                });
            }
        }
        imgSrc.push(el);
    };

    const createLine = (
        segments: number[],
        moveToX: number,
        moveToY: number,
        lineToX: number,
        lineToY: number,
        lineWidth: number,
        strokeStyle?: string | CanvasGradient | CanvasPattern,
        lineCap?: CanvasLineCap,
    ) => {
        ctx.beginPath();
        ctx.setLineDash(segments);
        ctx.moveTo(moveToX, moveToY);
        ctx.lineTo(lineToX, lineToY);
        ctx.strokeStyle = strokeStyle!;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = lineCap ? lineCap : 'round';
        ctx.stroke();
    };

    const createGeoTextileFilterLine = () => {
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        let strokeStyle: string | CanvasPattern;
        let lineWidth: number;

        for (let index = 0; index < 2; index++) {
            strokeStyle = index == 0 ? colorSecondLine : pat;
            lineWidth = index == 0 ? secondLineWidth + _secondLineOffset : _secondLineOffset;
            _canvasSectors.forEach(sector => {
                if (index == 0) {
                    if (sector.subSectors?.length! > 0) {
                        sector.subSectors?.forEach(x => {
                            if (x.withTwoLines) {
                                createLine(
                                    [],
                                    scale * (indicatorWidth / scale + borderWidth + x.moveToX),
                                    scale * (indicatorHeight / scale + x.moveToY),
                                    scale * (indicatorWidth / scale + borderWidth + x.lineToX),
                                    scale * (indicatorHeight / scale + x.lineToY),
                                    lineWidth,
                                    strokeStyle,
                                );
                            } else {
                                createLine(
                                    [],
                                    scale * (indicatorWidth / scale + borderWidth + x.moveToX),
                                    scale * (indicatorHeight / scale + x.moveToY),
                                    scale * (indicatorWidth / scale + borderWidth + x.lineToX),
                                    scale * (indicatorHeight / scale + x.lineToY),
                                    lineWidth,
                                    pat,
                                    'butt',
                                );
                            }
                        });
                    } else {
                        if (sector.withTwoLines) {
                            createLine(
                                [],
                                scale * (indicatorWidth / scale + borderWidth + sector.moveToX),
                                scale * (indicatorHeight / scale + sector.moveToY),
                                scale * (indicatorWidth / scale + borderWidth + sector.lineToX),
                                scale * (indicatorHeight / scale + sector.lineToY),
                                lineWidth,
                                strokeStyle,
                            );
                        }
                    }
                } else {
                    createLine(
                        [],
                        scale * (indicatorWidth / scale + borderWidth + sector.moveToX),
                        scale * (indicatorHeight / scale + sector.moveToY),
                        scale * (indicatorWidth / scale + borderWidth + sector.lineToX),
                        scale * (indicatorHeight / scale + sector.lineToY),
                        lineWidth,
                        strokeStyle,
                    );
                }
            });
        }
        ctx.restore();
    };

    const createSector = (canvasSector: ICanvasSector) => {
        let pat: CanvasPattern | string = canvasSector.img ? ctx.createPattern(canvasSector.img, 'repeat')! : canvasSector.color;
        if (canvasSector.error) {
            pat = errorColor;
        }
        let { moveToX, moveToY, lineToX, lineToY, channel, height, width } = canvasSector;
        if (canvasSector.subSectors?.length! > 0) {
            canvasSector.subSectors?.forEach(x => {
                let pat: CanvasPattern | string = x.img ? ctx.createPattern(x.img, 'repeat')! : x.color;
                if (x.error) {
                    pat = errorColor;
                }
                createLine(
                    [],
                    scale * (indicatorWidth / scale + borderWidth + x.moveToX),
                    scale * (indicatorHeight / scale + x.moveToY),
                    scale * (indicatorWidth / scale + borderWidth + x.lineToX),
                    scale * (indicatorHeight / scale + x.lineToY),
                    lineWidth,
                    pat,
                );
                if (height > 0) {
                    createIndicatorY(indicatorHeight / scale + x.moveToY, indicatorHeight / scale + x.lineToY, x.height, x.channel);
                }
                createIndicatorX(indicatorWidth / scale + borderWidth + x.moveToX, indicatorWidth / scale + borderWidth + x.lineToX, x.width, true, isThereSubsector);
            });
            createIndicatorX(indicatorWidth / scale + borderWidth + moveToX, indicatorWidth / scale + borderWidth + lineToX, width, false, isThereSubsector);
        } else {
            createLine(
                [],
                scale * (indicatorWidth / scale + borderWidth + moveToX),
                scale * (indicatorHeight / scale + moveToY),
                scale * (indicatorWidth / scale + borderWidth + lineToX),
                scale * (indicatorHeight / scale + lineToY),
                lineWidth,
                pat,
            );
            if (height > 0) {
                createIndicatorY(indicatorHeight / scale + moveToY, indicatorHeight / scale + lineToY, height, channel);
            }
            createIndicatorX(indicatorWidth / scale + borderWidth + moveToX, indicatorWidth / scale + borderWidth + lineToX, width, false, isThereSubsector);
        }
        createNumberSector(
            indicatorWidth / scale + borderWidth + moveToX,
            indicatorHeight / scale + moveToY,
            indicatorWidth / scale + borderWidth + lineToX,
            indicatorHeight / scale + lineToY,
            channel,
        );
    };

    const createNumberSector = (moveToX: number, moveToY: number, lineToX: number, lineToY: number, channel: number) => {
        let offset = -15;
        if (channel > lowerSector) {
            offset = +10;
        } else if (channel == lowerSector) {
            offset = 0;
        }
        ctx.save();
        ctx.fillStyle = 'white';
        ctx.font = font;
        ctx.fillText(channel.toString(), scale * (lineToX - (lineToX - moveToX) / 2) + offset, scale * (lineToY - (lineToY - moveToY) / 2) + 20);
        ctx.restore();
    };

    const createIndicatorY = (moveToY: number, lineToY: number, value: number, channel: number) => {
        let _moveToX = (indicatorWidth - 5) / scale;
        let _offsetText = -10;
        if (value % 1 !== 0) {
            _offsetText = -24;
        }
        if (channel > lowerSector) {
            _moveToX = indicatorWidth / scale + width + indicatorWidth / scale - indicatorWidth / scale + 5 / scale;
            _offsetText = 5;
        }
        let _lineToX = _moveToX;
        createLine([], scale * _moveToX - 2, scale * moveToY, scale * _moveToX + 2, scale * moveToY, indicatorLineWidth, colorIndicator);
        createLine([], scale * _moveToX, scale * moveToY, scale * _lineToX, scale * lineToY, indicatorLineWidth, colorIndicator);
        createLine([], scale * _moveToX - 2, scale * lineToY, scale * _moveToX + 2, scale * lineToY, indicatorLineWidth, colorIndicator);
        ctx.font = font;
        ctx.fillText(roundToTwo(value).toString(), scale * _moveToX + _offsetText, scale * (lineToY - (lineToY - moveToY) / 2));
    };

    const createIndicatorX = (moveToX: number, lineToX: number, value: number, isSubsector: boolean, isThereSubsector: boolean) => {
        let _lineToY = indicatorHeight / scale + heightAllSector + heightSoil + indicatorHeight / scale - 50 / scale;
        let _offset = 0;
        if (isThereSubsector && !isSubsector) {
            _offset = 7;
            _lineToY = indicatorHeight / scale + heightAllSector + heightSoil + indicatorHeight / scale - 40 / scale;
        }
        createLine([], scale * moveToX, scale * _lineToY + _offset - 2, scale * moveToX, scale * _lineToY + _offset + 2, indicatorLineWidth, colorIndicator);
        createLine([], scale * moveToX, scale * _lineToY + _offset, scale * lineToX, scale * _lineToY + _offset, indicatorLineWidth, colorIndicator);
        createLine([], scale * lineToX, scale * _lineToY + _offset - 2, scale * lineToX, scale * _lineToY + _offset + 2, indicatorLineWidth, colorIndicator);
        ctx.font = font;
        ctx.fillText(roundToTwo(value).toString(), scale * (moveToX - (moveToX - lineToX) / 2) - 5, scale * _lineToY + _offset + 12);
    };

    const createWaterSection = () => {
        let lowestPointY: number = 0;
        let heightLeftChannel: number = 0;
        let heightRightChannel: number = 0;
        let minWaterAllowedY: number = 0;

        _canvasSectors.forEach(x => {
            let maxY = Math.max(x.moveToY, x.lineToY);
            lowestPointY = maxY > lowestPointY ? maxY : lowestPointY;
            if (x.channel <= lowerSector) {
                heightLeftChannel += x.lineToY - x.moveToY;
            } else {
                heightRightChannel += x.moveToY - x.lineToY;
            }
        });

        if (heightLeftChannel <= heightRightChannel) {
            minWaterAllowedY = _canvasSectors[0].moveToY;
        } else {
            minWaterAllowedY = _canvasSectors[_canvasSectors.length - 1].lineToY;
        }

        let waterHeight: number = lowestPointY - minWaterAllowedY;
        ctx.save();
        ctx.beginPath();
        ctx.fillStyle = waterColor;
        ctx.moveTo((scale * indicatorWidth) / scale, scale * (indicatorHeight / scale + minWaterAllowedY + freeboard!));
        ctx.lineTo(scale * (indicatorWidth / scale + borderWidth + widthAllSector), scale * (indicatorHeight / scale + minWaterAllowedY + freeboard!));
        ctx.lineTo(scale * (indicatorWidth / scale + borderWidth + widthAllSector), scale * (indicatorHeight / scale + heightAllSector + heightSoil));
        ctx.lineTo(scale * (indicatorWidth / scale + borderWidth), scale * (indicatorHeight / scale + heightAllSector + heightSoil));
        ctx.fill();
        ctx.restore();
        createIndicatorWaterY(indicatorHeight / scale + minWaterAllowedY + freeboard!, indicatorHeight / scale + minWaterAllowedY + waterHeight, waterDepth!);
        createFreeboardY(indicatorHeight / scale + minWaterAllowedY, indicatorHeight / scale + minWaterAllowedY + freeboard!, freeboard!);
    };

    const createIndicatorWaterY = (moveToY: number, lineToY: number, value: number) => {
        let lowerSectorEl = _canvasSectors.find(x => x.channel == lowerSector);
        let offset = (lowerSectorEl!.lineToX - lowerSectorEl!.moveToX) / 2;
        createLine(
            [],
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) - 2,
            scale * moveToY,
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) + 2,
            scale * moveToY,
            indicatorLineWidth,
            colorIndicator,
        );
        createLine(
            [],
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset),
            scale * moveToY,
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset),
            scale * lineToY - lineWidth / 2 - 1,
            indicatorLineWidth,
            colorIndicator,
        );
        createLine(
            [],
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) - 2,
            scale * lineToY - lineWidth / 2 - 1,
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) + 2,
            scale * lineToY - lineWidth / 2 - 1,
            indicatorLineWidth,
            colorIndicator,
        );
        ctx.font = font;
        ctx.fillText(roundToTwo(value).toString(), scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) + 3, scale * (lineToY - (lineToY - moveToY) / 2));
    };

    const createFreeboardY = (moveToY: number, lineToY: number, value: number) => {
        let lowerSectorEl = _canvasSectors.find(x => x.channel == lowerSector);
        let offset = (lowerSectorEl!.lineToX - lowerSectorEl!.moveToX) / 2;
        createLine(
            [],
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) - 2,
            scale * moveToY,
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) + 2,
            scale * moveToY,
            indicatorLineWidth,
            colorIndicator,
        );
        createLine(
            [],
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset),
            scale * moveToY,
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset),
            scale * lineToY,
            indicatorLineWidth,
            colorIndicator,
        );
        createLine(
            [],
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) - 2,
            scale * lineToY,
            scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) + 2,
            scale * lineToY,
            indicatorLineWidth,
            colorIndicator,
        );
        ctx.font = font;
        ctx.fillText(roundToTwo(value).toString(), scale * (indicatorWidth / scale + borderWidth + lowerSectorEl!.moveToX + offset) + 3, scale * (lineToY - (lineToY - moveToY) / 2));
    };

    const createChannel = () => {
        let canvasSectorFirst = _canvasSectors[0];
        let canvasSector3 = _canvasSectors.find(x => x.channel == lowerSector - 1)!;
        let canvasSector5 = _canvasSectors.find(x => x.channel == lowerSector + 1)!;
        let canvasSectorLast = _canvasSectors[_canvasSectors.length - 1];

        let isThereLeftChannel = _canvasSectors.some(x => x.channel < lowerSector - 1);
        let isThereRightChannel = _canvasSectors.some(x => x.channel > lowerSector + 1);

        if (isThereLeftChannel) {
            createLine(
                [5, 5],
                scale * (indicatorWidth / scale + borderWidth + canvasSectorFirst.moveToX),
                scale * (indicatorHeight / scale + canvasSectorFirst.moveToY),
                scale * (indicatorWidth / scale + borderWidth + canvasSectorFirst.moveToX),
                0,
                dottedLineWidth,
                colordottedLine,
            );
            ctx.font = font;
            ctx.fillText(
                t('canvas_left_bank'),
                scale * (indicatorWidth / scale + borderWidth + canvasSector3.moveToX - (canvasSector3.moveToX - canvasSectorFirst.moveToX) / 2) - 20,
                (scale * indicatorHeight) / scale / 2,
            );
        }
        createLine(
            [5, 5],
            scale * (indicatorWidth / scale + borderWidth + canvasSector3.moveToX),
            scale * (indicatorHeight / scale + canvasSector3?.moveToY),
            scale * (indicatorWidth / scale + borderWidth + canvasSector3.moveToX),
            0,
            dottedLineWidth,
            colordottedLine,
        );
        createLine(
            [5, 5],
            scale * (indicatorWidth / scale + borderWidth + canvasSector5.lineToX),
            scale * (indicatorHeight / scale + canvasSector5.lineToY),
            scale * (indicatorWidth / scale + borderWidth + canvasSector5.lineToX),
            0,
            dottedLineWidth,
            colordottedLine,
        );
        if (isThereRightChannel) {
            createLine(
                [5, 5],
                scale * (indicatorWidth / scale + borderWidth + canvasSectorLast.lineToX),
                scale * (indicatorHeight / scale + canvasSectorLast.lineToY),
                scale * (indicatorWidth / scale + borderWidth + canvasSectorLast.lineToX),
                0,
                dottedLineWidth,
                colordottedLine,
            );
            ctx.font = font;
            ctx.fillText(
                t('canvas_right_bank'),
                scale * (indicatorWidth / scale + borderWidth + canvasSectorLast.lineToX - (canvasSectorLast.lineToX - canvasSector5.lineToX) / 2) - 25,
                (scale * indicatorHeight) / scale / 2,
            );
        }
        ctx.font = font;
        ctx.fillText(
            t('general_results_main_channel'),
            scale * (indicatorWidth / scale + borderWidth + canvasSector5.lineToX - (canvasSector5.lineToX - canvasSector3.moveToX) / 2) - 30,
            (scale * indicatorHeight) / scale / 2,
        );
    };

    const createSectors = () => {
        isThereSubsector = _canvasSectors.some(x => x.subSectors?.length! > 0);
        let sortCanvasSectors = [
            ..._canvasSectors.filter(x => x.channel % 2 === 0).sort((a, b) => a.channel - b.channel),
            ..._canvasSectors.filter(x => x.channel % 2 !== 0).sort((a, b) => a.channel - b.channel),
        ];
        canvasSectors = sortCanvasSectors;
        _scale = props.scale;
        sortCanvasSectors.forEach(x => createSector(x));
    };

    const createSoil = () => {
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.beginPath();
        _canvasSectors.forEach((sector, index) => {
            if (index == 0) {
                ctx.moveTo((scale * indicatorWidth) / scale, (scale * indicatorHeight) / scale);
                ctx.lineTo(scale * (indicatorWidth / scale + borderWidth), (scale * indicatorHeight) / scale);
                ctx.lineTo(scale * (indicatorWidth / scale + borderWidth + sector.lineToX), scale * (indicatorHeight / scale + sector.lineToY));
            }
            ctx.lineTo(scale * (indicatorWidth / scale + borderWidth + sector.lineToX), scale * (indicatorHeight / scale + sector.lineToY));
            if (_canvasSectors.length == index + 1) {
                ctx.lineTo(scale * (indicatorWidth / scale + borderWidth + sector.lineToX), scale * (indicatorHeight / scale + sector.lineToY));
                ctx.lineTo(scale * (indicatorWidth / scale + borderWidth + widthAllSector + borderWidth), scale * (indicatorHeight / scale + sector.lineToY));
            }
        });
        ctx.lineTo(scale * (indicatorWidth / scale + borderWidth + widthAllSector + borderWidth), scale * (indicatorHeight / scale + heightAllSector + heightSoil));
        ctx.lineTo((scale * indicatorWidth) / scale, scale * (indicatorHeight / scale + heightAllSector + heightSoil));
        ctx.lineTo((scale * indicatorWidth) / scale, (scale * indicatorHeight) / scale);
        ctx.strokeStyle = 'transparent';
        ctx.stroke();
        ctx.save();
        ctx.clip();
        ctx.fillStyle = pat;
        ctx.fill();
    };

    const getCanvasSectors = () => {
        let endSectorX = 0;
        let endSectorY = 0;
        _canvasSectors = sectorsToShow.map((y, i) => {
            let isAfterLowerSector = y.channel > lowerSector;

            let imgCanvasObj = imgSrc.find(x => x.channel == y.channel)!;

            let height = isAfterLowerSector ? -y.height : y.height;

            let _moveToX = i === 0 ? 0 : endSectorX;
            let _moveToY = i === 0 ? 0 : endSectorY;
            let _lineToX = i === 0 ? endSectorX + y.width : endSectorX + y.width;
            let _lineToY = i === 0 ? endSectorY + height : endSectorY + height;

            let moveToXSubSectorRight: number, moveToYSubSectorRight: number;
            let lineToXSubSectorLeft: number, lineToYSubSectorLeft: number;

            let sector: ICanvasSector = {
                channel: y.channel,
                height: y.height,
                width: y.width,
                angle: y.angle,
                length: y.length,
                moveToX: _moveToX,
                moveToY: _moveToY,
                lineToX: _lineToX,
                lineToY: _lineToY,
                img: imgCanvasObj.imgSrc!,
                color: y.material!.color,
                error: y.error,
                withTwoLines: y.material?.isGeotextileFilter,
                subSectors: y.subSectors?.map(x => {
                    let cx = _lineToX - _moveToX;
                    let cy = _lineToY - _moveToY;
                    let sectorLength = y.length;
                    let lengthSubSector = x.length!;
                    let lengthSubSectorCx = (lengthSubSector / sectorLength) * cx;
                    let lengthSubSectorCy = (lengthSubSector / sectorLength) * cy;
                    let subSec: ICanvasSector = {
                        channel: y.channel,
                        height: parseFloat((x.length! * Math.sin((y.angle * Math.PI) / 180)).toFixed(2)),
                        width: parseFloat((x.length! * Math.cos((y.angle * Math.PI) / 180)).toFixed(2)),
                        length: x.length!,
                        position: x.position,
                        angle: y.angle,
                        moveToX: 0,
                        moveToY: 0,
                        lineToX: 0,
                        lineToY: 0,
                        withTwoLines: x.material?.isGeotextileFilter,
                        img: imgCanvasObj?.subSector?.find(y => y.position == x.position)?.imgSrc!,
                        color: x.material?.color!,
                        error: x.error,
                    };

                    switch (x.position) {
                        case SubSectorPosition.Right:
                            if (y.subSectors?.length == 1) {
                                subSec.moveToX = _moveToX;
                                subSec.moveToY = _moveToY;
                                subSec.lineToX = _lineToX;
                                subSec.lineToY = _lineToY;
                            } else {
                                moveToXSubSectorRight = _lineToX - lengthSubSectorCx;
                                moveToYSubSectorRight = _lineToY - lengthSubSectorCy;

                                subSec.moveToX = moveToXSubSectorRight;
                                subSec.moveToY = moveToYSubSectorRight;
                                subSec.lineToX = _lineToX;
                                subSec.lineToY = _lineToY;
                            }
                            break;
                        case SubSectorPosition.Left:
                            if (y.subSectors?.length == 2) {
                                subSec.moveToX = _moveToX;
                                subSec.moveToY = _moveToY;
                                subSec.lineToX = moveToXSubSectorRight;
                                subSec.lineToY = moveToYSubSectorRight;
                            } else {
                                lineToXSubSectorLeft = _moveToX + lengthSubSectorCx;
                                lineToYSubSectorLeft = _moveToY + lengthSubSectorCy;

                                subSec.moveToX = _moveToX;
                                subSec.moveToY = _moveToY;
                                subSec.lineToX = lineToXSubSectorLeft;
                                subSec.lineToY = lineToYSubSectorLeft;
                            }
                            break;
                        case SubSectorPosition.Center:
                            subSec.moveToX = lineToXSubSectorLeft;
                            subSec.moveToY = lineToYSubSectorLeft;
                            subSec.lineToX = moveToXSubSectorRight;
                            subSec.lineToY = moveToYSubSectorRight;
                            break;

                        default:
                            break;
                    }
                    return subSec;
                }),
            };
            endSectorX = sector.lineToX;
            endSectorY = sector.lineToY;
            return sector;
        });
    };

    return <canvas className={className} ref={canvas} width={indicatorWidth + scale * width + indicatorWidth} height={indicatorHeight + scale * height + indicatorHeight}></canvas>;
};

export default Canvas;
